import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCountdown } from '@hostfully/ui-components';
import useConfirmMutation, {
  type ConfirmSubmitValues,
} from './queries/useConfirmMutation';
import useRegistrationResendMutation from './queries/useRegistrationResendMutation';
import { REGISTER_RESEND_COOLDOWN_STORAGE_KEY } from './Register.constants';
import { MFA_RESEND_COOLDOWN_SECONDS } from '@/pages/login/mfa/MFA.constants';

interface UseRegisterConfirmSubmitArgs {
  registrationToken: string;
}

const CONFIRMATION_FIELD = 'confirmationCode';

const useRegisterConfirmSubmit = ({
  registrationToken,
}: UseRegisterConfirmSubmitArgs) => {
  const { t } = useTranslation();
  const { setError, clearErrors } = useFormContext();
  const cooldown = useCountdown({
    storageKey: REGISTER_RESEND_COOLDOWN_STORAGE_KEY,
    initialSeconds: MFA_RESEND_COOLDOWN_SECONDS,
  });

  const setConfirmationError = (message: string) =>
    setError(CONFIRMATION_FIELD, { type: 'manual', message });

  const confirmMutation = useConfirmMutation({
    registrationToken,
    onError: (errorCode) =>
      setConfirmationError(
        t('pageRegister.errorConfirmCode', { context: errorCode }),
      ),
  });

  const resendMutation = useRegistrationResendMutation({
    registrationToken,
    onSuccess: () => cooldown.start(MFA_RESEND_COOLDOWN_SECONDS),
    onError: (errorCode) =>
      setConfirmationError(
        t('pageRegister.errorResendCode', { context: errorCode }),
      ),
  });

  const handleSubmitConfirm = (values: ConfirmSubmitValues) => {
    clearErrors(CONFIRMATION_FIELD);
    confirmMutation.mutate(values);
  };

  const handleResendCode = () => {
    clearErrors(CONFIRMATION_FIELD);
    resendMutation.mutate();
  };

  return {
    handleSubmitConfirm,
    handleResendCode,
    isSubmitting: confirmMutation.isPending,
    isResending: resendMutation.isPending,
    resendCooldownSeconds: cooldown.remainingSeconds,
  };
};

export default useRegisterConfirmSubmit;
